import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../../Config/axiosInstance";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import "./FormOfferRegistry.css";
import DatePicker from "react-datepicker";
import { ro } from "date-fns/locale";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import { NumericFormat } from "react-number-format";
import { useDropzone } from "react-dropzone";

const animatedComponents = makeAnimated();

// Setăm limba română pentru calendar.
registerLocale("ro", ro);

// Formatează data pentru afișare.
const formatDate = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

// Transformă un string de tip dată într-un obiect Date, ca să poată fi folosit în DatePicker.
const formatDateForPicker = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString);
};

// Formatează sumele pentru afișare cu 2 zecimale.
const formatAmountDisplay = (value) => {
  const n = Number(value);

  if (!Number.isFinite(n)) return "0,00";

  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

// Stiluri pentru dropdown-urile react-select, ca să apară corect peste restul elementelor din pagină.
const selectPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  menu: (base) => ({ ...base, zIndex: 999999 }),
};

// Opțiunile de status disponibile pentru ofertă.
const statusOptions = [
  { value: "Under client review", label: "Under client review" },
  { value: "Contract signed", label: "Contract signed" },
  { value: "On hold", label: "On hold" },
  { value: "Offer closed", label: "Offer closed" },
];

// Opțiunile de valabilitate disponibile.
const validityOptions = [
  { value: "10", label: "10 days" },
  { value: "30", label: "30 days" },
  { value: "60", label: "60 days" },
  { value: "90", label: "90 days" },
];

const EditOfferRegistry = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [systemCurrencyId, setSystemCurrencyId] = useState(null);
  const [systemCurrencyLabel, setSystemCurrencyLabel] = useState(null);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [isOppLoading, setIsOppLoading] = useState(false);

  const [selNumber, setSelNumber] = useState("");
  const [initialNumber, setInitialNumber] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [selClientId, setSelClientId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selOfferDate, setSelOfferDate] = useState("");
  const [selStatus, setSelStatus] = useState(null);
  const [selValidity, setSelValidity] = useState(null);
  const [selDescription, setSelDescription] = useState("");
  const [selectedOpps, setSelectedOpps] = useState([]);
  const [lines, setLines] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [clientContacts, setClientContacts] = useState([]);
  const [selectedContactPerson, setSelectedContactPerson] = useState(null);
  const [selOfferObject, setSelOfferObject] = useState("");
  const [selExecutionTerm, setSelExecutionTerm] = useState("");

  const [customFields, setCustomFields] = useState([]);
  const [files, setFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [dropzoneKey, setDropzoneKey] = useState(Date.now());
  const [errorNotification, setErrorNotification] = useState("");

  // Controlează secțiunile deschise / închise.
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    opportunities: true,
    additionalFields: true,
    attachments: true,
  });

  const RAW_API_BASE =
    process.env.REACT_APP_API_URL ||
    axiosInstance.defaults.baseURL ||
    window.location.origin;

  const API_BASE_URL = String(RAW_API_BASE)
    .replace(/\/api\/?$/, "")
    .replace(/\/+$/, "");

  // Deschide sau închide o secțiune din formular.
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const showError = useCallback((message, timeout = 5000) => {
    setErrorNotification(message);
    setTimeout(() => setErrorNotification(""), timeout);
  }, []);

  // Grupează cursurile valutare după monedă și le sortează descrescător după dată.
  const exchangeOptionsByCurrencyId = useMemo(() => {
    const map = new Map();

    for (const rate of exchangeRates) {
      const currencyId = rate.currency;
      if (!map.has(currencyId)) map.set(currencyId, []);

      map.get(currencyId).push({
        value: rate.id,
        label: `${rate.value} (${formatDate(rate.date)})`,
        shortLabel: `${rate.value}`,
        rateValue: Number(rate.value),
        rateDate: rate.date,
      });
    }

    for (const [key, arr] of map.entries()) {
      arr.sort((a, b) => new Date(b.rateDate) - new Date(a.rateDate));
      map.set(key, arr);
    }

    return map;
  }, [exchangeRates]);

  /*
    Calculează totalul live al ofertei.
    Dacă toate liniile sunt în aceeași monedă, afișăm totalul în acea monedă.
    Dacă sunt mai multe monede, afișăm totalul convertit în moneda de sistem.
   */
  const liveTotalDisplay = useMemo(() => {
    if (!lines.length) return "0,00";

    const validLines = lines.filter((line) => {
      const hasSum =
        line.sum !== null && line.sum !== undefined && line.sum !== "";
      const hasCurrency = !!line.currency?.value;
      return hasSum && hasCurrency;
    });

    if (!validLines.length) return "0,00";

    const distinctCurrencyIds = [
      ...new Set(validLines.map((line) => line.currency.value)),
    ];

    if (distinctCurrencyIds.length === 1) {
      const total = validLines.reduce((acc, line) => {
        const val = Number(line.sum);
        return acc + (Number.isFinite(val) ? val : 0);
      }, 0);

      const currencyLabel = validLines[0]?.currency?.label || "";
      return `${formatAmountDisplay(total)} ${currencyLabel}`.trim();
    }

    const totalSystem = validLines.reduce((acc, line) => {
      const val = Number(line.calculated);
      return acc + (Number.isFinite(val) ? val : 0);
    }, 0);

    return `${formatAmountDisplay(totalSystem)} ${systemCurrencyLabel || ""}`.trim();
  }, [lines, systemCurrencyLabel]);

  // Returnează cursul valutar implicit pentru o monedă, de regulă cel mai recent.
  const getDefaultExchangeRateOption = (currencyId) => {
    const arr = exchangeOptionsByCurrencyId.get(currencyId) || [];
    return arr.length ? arr[0] : null;
  };

  // Calculează suma unei linii în moneda de sistem.
  const computeSystemAmount = useCallback(
    (sum, currencyId, exchangeOpt, currentSystemCurrencyId = systemCurrencyId) => {
      if (sum == null || sum === "") return "";

      const numericSum = Number(sum);
      if (!Number.isFinite(numericSum)) return "";

      if (!currencyId || !currentSystemCurrencyId) return "";

      if (Number(currencyId) === Number(currentSystemCurrencyId)) {
        return numericSum.toFixed(2);
      }

      const rateValue = exchangeOpt?.rateValue;
      if (!rateValue || !Number.isFinite(rateValue)) return "";

      return (numericSum * rateValue).toFixed(2);
    },
    [systemCurrencyId]
  );

  // Construiește un map de opțiuni pentru cursuri valutare, grupate pe monedă.
  const buildExchangeOptionsMap = useCallback((rates) => {
    const map = new Map();

    for (const rate of rates) {
      const currencyId = rate.currency;
      if (!map.has(currencyId)) map.set(currencyId, []);

      map.get(currencyId).push({
        value: rate.id,
        label: `${rate.value} (${formatDate(rate.date)})`,
        shortLabel: `${rate.value}`,
        rateValue: Number(rate.value),
        rateDate: rate.date,
      });
    }

    for (const [key, arr] of map.entries()) {
      arr.sort((a, b) => new Date(b.rateDate) - new Date(a.rateDate));
      map.set(key, arr);
    }

    return map;
  }, []);

  const matchPageProtocol = (url) => {
    if (!url) return url;

    if (typeof window !== "undefined" && window.location.protocol === "https:") {
      return url.replace(/^http:\/\//i, "https://");
    }

    return url;
  };

  const getFileUrl = (file) => {
    if (!file) return null;

    if (file.file_url) return matchPageProtocol(file.file_url);
    if (file.url) return matchPageProtocol(file.url);

    if (file.file) {
      const rawPath = String(file.file);

      if (/^https?:\/\//i.test(rawPath)) {
        return matchPageProtocol(rawPath);
      }

      const normalizedPath = rawPath.startsWith("/")
        ? rawPath
        : `/media/${rawPath.replace(/^media\//, "")}`;

      return matchPageProtocol(`${API_BASE_URL}${normalizedPath}`);
    }

    return null;
  };

  const getExistingFileName = (file) => {
    if (file?.filename) return file.filename;

    if (typeof file?.file === "string") {
      const parts = file.file.split("/");
      return parts[parts.length - 1];
    }

    return "download";
  };

  const isPdfExistingFile = (file) => {
    const filename = getExistingFileName(file).toLowerCase();
    return filename.endsWith(".pdf");
  };

  const getFileIcon = (file, isExisting = false) => {
    if (isExisting) {
      const filename = file.filename || file.file?.name || file.file || "";
      const extension = filename.split(".").pop()?.toLowerCase() || "";

      if (extension === "pdf") {
        return <PictureAsPdfIcon style={{ fontSize: 24, color: "#d32f2f" }} />;
      } else if (["doc", "docx"].includes(extension)) {
        return <DescriptionIcon style={{ fontSize: 24, color: "#1976d2" }} />;
      } else if (["xls", "xlsx"].includes(extension)) {
        return <DescriptionIcon style={{ fontSize: 24, color: "#2e7d32" }} />;
      } else if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
        return <InsertDriveFileIcon style={{ fontSize: 24, color: "#9c27b0" }} />;
      }
      return <InsertDriveFileIcon style={{ fontSize: 24, color: "#757575" }} />;
    }

    const fileType = file.type;

    if (fileType === "application/pdf") {
      return <PictureAsPdfIcon style={{ fontSize: 24, color: "#d32f2f" }} />;
    } else if (
      fileType === "application/msword" ||
      fileType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return <DescriptionIcon style={{ fontSize: 24, color: "#1976d2" }} />;
    } else if (
      fileType === "application/vnd.ms-excel" ||
      fileType ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      return <DescriptionIcon style={{ fontSize: 24, color: "#2e7d32" }} />;
    } else if (fileType.startsWith("image/")) {
      return <InsertDriveFileIcon style={{ fontSize: 24, color: "#9c27b0" }} />;
    }

    return <InsertDriveFileIcon style={{ fontSize: 24, color: "#757575" }} />;
  };

  const handleFileClick = async (file, isExisting = false) => {
    if (isExisting) {
      const fileUrl = getFileUrl(file);

      if (!fileUrl) {
        showError("File URL not available");
        return;
      }

      try {
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error("Failed to fetch file");
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const filename = getExistingFileName(file);

        if (isPdfExistingFile(file)) {
          const newWindow = window.open();
          if (!newWindow) {
            showError("Popup blocked. Please allow popups for PDF preview.");
            URL.revokeObjectURL(objectUrl);
            return;
          }

          newWindow.document.write(`
            <html>
              <head>
                <title>${filename}</title>
                <style>
                  body { margin: 0; height: 100vh; }
                  embed { width: 100%; height: 100%; }
                </style>
              </head>
              <body>
                <embed src="${objectUrl}" type="application/pdf" />
              </body>
            </html>
          `);
          newWindow.document.close();

          setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
        } else {
          const a = document.createElement("a");
          a.href = objectUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        }
      } catch (error) {
        console.error("Error opening existing file:", error);
        showError("Could not open/download the selected file.");
      }

      return;
    }

    const url = URL.createObjectURL(file);

    if (file.type === "application/pdf") {
      const newWindow = window.open();
      if (!newWindow) {
        showError("Popup blocked. Please allow popups for PDF preview.");
        URL.revokeObjectURL(url);
        return;
      }

      newWindow.document.write(`
                <html>
          <head>
            <title>${file.name}</title>
            <style>
              body { margin: 0; height: 100vh; }
              embed { width: 100%; height: 100%; }
            </style>
          </head>
          <body>
            <embed src="${url}" type="application/pdf" />
          </body>
        </html>
      `);
      newWindow.document.close();

      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };

  const validateFiles = () => {
    if (files.length === 0) return true;

    const MAX_FILE_SIZE = 100 * 1024 * 1024;
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      showError(
        `The following files exceed the 100MB limit:\n${oversizedFiles
          .map((f) => f.name)
          .join("\n")}`,
        5000
      );
      return false;
    }

    return true;
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
      "image/jpeg": [],
      "image/png": [],
      "application/vnd.ms-excel": [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
      "text/plain": [],
    },
    multiple: true,
    maxSize: 1024 * 1024 * 100,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const tooLarge = rejectedFiles.filter(
          (f) => f.errors[0]?.code === "file-too-large"
        );
        if (tooLarge.length > 0) {
          showError(`File ${tooLarge[0].file.name} exceeds 100MB.`, 3000);
        }
      }

      if (acceptedFiles.length > 0) {
        setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
      }
    },
  });

  const handleRemoveFile = (indexToRemove, isExisting = false) => {
    if (isExisting) {
      setExistingAttachments((prev) =>
        prev.filter((_, index) => index !== indexToRemove)
      );
    } else {
      setFiles((prevFiles) => {
        const updatedFiles = prevFiles.filter((_, index) => index !== indexToRemove);
        if (updatedFiles.length === 0) {
          setDropzoneKey(Date.now());
        }
        return updatedFiles;
      });
    }
  };

  // Aduce oportunitățile disponibile pentru clientul selectat.
  const fetchOpportunitiesByClient = useCallback(
    async (clientId) => {
      if (!clientId) return [];

      const res = await axiosInstance.get(
        `/opportunities/?client=${clientId}&available_for_offer=1&offer_id=${id}`
      );

      return (res.data || []).map((opp) => ({
        value: opp.id,
        label: `${opp.code_opportunity} - ${opp.title_opportunity}`,
        project_code_text: opp.project_code_label ?? "",
        subproject_code_text: opp.subproject_code_label ?? "",
      }));
    },
    [id]
  );

  const fetchContactsByClient = useCallback(async (clientId) => {
    if (!clientId) return [];

    const res = await axiosInstance.get(`/client-contacts/?client=${clientId}`);

    return (res.data || []).map((contact) => ({
      value: contact.id,
      label: `${contact.name}${contact.position ? ` - ${contact.position}` : ""}`,
    }));
  }, []);

  const addCustomField = () => {
    setCustomFields((prev) => [
      ...prev,
      {
        localId: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        field_title: "",
        field_content: "",
      },
    ]);

    if (fieldErrors.custom_fields) {
      setFieldErrors((prev) => ({
        ...prev,
        custom_fields: "",
      }));
    }
  };

  const updateCustomField = (localId, patch) => {
    setCustomFields((prev) =>
      prev.map((field) =>
        field.localId === localId ? { ...field, ...patch } : field
      )
    );

    if (fieldErrors.custom_fields) {
      setFieldErrors((prev) => ({
        ...prev,
        custom_fields: "",
      }));
    }
  };

  const removeCustomField = (localId) => {
    setCustomFields((prev) =>
      prev.filter((field) => field.localId !== localId)
    );
  };


  /*
    La încărcarea paginii:
    - aduce toate listele de bază
    - aduce oferta curentă
    - populează formularul cu datele existente
    - încarcă oportunitățile deja legate de ofertă
   */
  useEffect(() => {
    const loadPage = async () => {
      setIsLoading(true);

      try {
        const [clientsRes, curRes, exRes, offerRes, sysRes] = await Promise.all([
          axiosInstance.get("/clients/"),
          axiosInstance.get("/currencies/"),
          axiosInstance.get("/exchange-rate/"),
          axiosInstance.get(`/offer-register/${id}/`),
          axiosInstance.get("/system-currency/1/"),
        ]);

        const clientOptions = (clientsRes.data || []).map((c) => ({
          value: c.id,
          label: c.name,
        }));
        setClients(clientOptions);

        const currencyOptions = (curRes.data || []).map((c) => ({
          value: c.id,
          label: c.abbreviation,
        }));
        setCurrencies(currencyOptions);

        const exchangeRateList = (exRes.data || []).sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        setExchangeRates(exchangeRateList);

        const localExchangeOptionsMap = buildExchangeOptionsMap(exchangeRateList);

        const sysCurrencyId = sysRes.data?.currency ?? null;
        const sysCurrencyLabel = sysRes.data?.currency_label ?? null;

        setSystemCurrencyId(sysCurrencyId);
        setSystemCurrencyLabel(sysCurrencyLabel);

        const data = offerRes.data;

        setSelNumber(data.offer_code || "");
        setInitialNumber(data.offer_code || "");
        setSelOfferDate(data.offer_date || "");
        setSelectedDate(formatDateForPicker(data.offer_date));
        setSelDescription(data.description || "");
        setSelClientId(data.client || null);

        setSelOfferObject(data.offer_object || "");

        setSelExecutionTerm(data.execution_term || "");

        setCustomFields(
          Array.isArray(data.custom_fields)
            ? data.custom_fields.map((field, index) => ({
              localId:
                field.id != null
                  ? `existing_${field.id}`
                  : `${Date.now()}_${index}_${Math.random()
                    .toString(16)
                    .slice(2)}`,
              id: field.id,
              field_title: field.field_title || "",
              field_content: field.field_content || "",
              order: field.order ?? index,
            }))
            : []
        );

        const clientOpt =
          clientOptions.find((c) => c.value === data.client) || null;
        setSelectedClient(clientOpt);

        setSelStatus(
          data.status ? { value: data.status, label: data.status } : null
        );

        setSelValidity(
          data.valability
            ? validityOptions.find((v) => v.value === String(data.valability)) || {
              value: String(data.valability),
              label: `${data.valability} days`,
            }
            : null
        );

        if (Array.isArray(data.attach_files)) {
          setExistingAttachments(
            data.attach_files.map((att) => ({
              id: att.id,
              filename: att.filename || att.file?.split("/").pop() || "Unknown file",
              file: att.file,
              file_url: att.file_url || att.url || null,
              uploaded_at: att.uploaded_at,
            }))
          );
        } else {
          setExistingAttachments([]);
        }

        const opportunityOptions = data.client
          ? await fetchOpportunitiesByClient(data.client)
          : [];

        const contactOptions = data.client ? await fetchContactsByClient(data.client) : [];
        setClientContacts(contactOptions);

        const selectedContactId = Array.isArray(data.contact_persons)
          ? data.contact_persons[0]
          : data.contact_persons;

        setSelectedContactPerson(
          selectedContactId
            ? contactOptions.find((c) => c.value === selectedContactId) || null
            : null
        );
        const selected = [];
        const newLines = [];
        const missingOpportunityOptions = [];

        for (const item of data.offer_opportunities || []) {
          let opp = opportunityOptions.find((o) => o.value === item.opportunity);

          if (!opp) {
            opp = {
              value: item.opportunity,
              label: item.opportunity_label
                ? `${item.opportunity_label}`
                : `Opportunity ${item.opportunity}`,
              project_code_text: item.project_code_label || "",
              subproject_code_text: item.subproject_code_label || "",
            };

            missingOpportunityOptions.push(opp);
          }

          selected.push(opp);

          const currency =
            item.currency != null
              ? currencyOptions.find((c) => c.value === item.currency) || null
              : null;

          let exchangeRate = null;
          if (item.exchange_rate && currency) {
            const possibleRates =
              localExchangeOptionsMap.get(currency.value) || [];
            exchangeRate =
              possibleRates.find((r) => r.value === item.exchange_rate) || null;
          }

          const calculated =
            item.sum_system_currency != null &&
              item.sum_system_currency !== ""
              ? Number(item.sum_system_currency).toFixed(2)
              : item.sum != null && item.sum !== ""
                ? computeSystemAmount(
                  item.sum,
                  currency?.value ?? null,
                  exchangeRate,
                  sysCurrencyId
                )
                : "";

          newLines.push({
            opportunityId: item.opportunity,
            opportunityLabel: opp.label,
            project_code_text: opp.project_code_text || "",
            subproject_code_text: opp.subproject_code_text || "",
            sum: item.sum,
            currency,
            exchange_rate: exchangeRate,
            calculated,
          });
        }

        const mergedOpportunityOptions = [
          ...opportunityOptions,
          ...missingOpportunityOptions.filter(
            (missing) =>
              !opportunityOptions.some((opt) => opt.value === missing.value)
          ),
        ];

        setOpportunities(mergedOpportunityOptions);
        setSelectedOpps(selected);
        setLines(newLines);
      } catch (e) {
        console.error("load edit page error", e);
        showError("Error loading offer data");
        navigate(-1);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      loadPage();
    }
  }, [
    id,
    navigate,
    buildExchangeOptionsMap,
    fetchOpportunitiesByClient,
    fetchContactsByClient,
    computeSystemAmount,
    showError,
  ]);

  /* Când se schimbă clientul:
    - dacă este alt client, resetăm oportunitățile și liniile
    - încărcăm oportunitățile disponibile pentru noul client
   */
  const handleClientChange = async (opt) => {
    const newId = opt ? opt.value : null;
    const isSameClient = newId === selClientId;

    setSelectedClient(opt);
    setSelClientId(newId);

    setFieldErrors((prev) => ({
      ...prev,
      selClientId: "",
      lines: "",
    }));

    if (isSameClient) return;

    setSelectedOpps([]);
    setLines([]);
    setOpportunities([]);
    setClientContacts([]);
    setSelectedContactPerson(null);

    if (!newId) return;

    setIsOppLoading(true);
    try {
      const opts = await fetchOpportunitiesByClient(newId);
      setOpportunities(opts);
      const contacts = await fetchContactsByClient(newId);
      setClientContacts(contacts);
    } catch (e) {
      console.error("opp fetch error", e);
      setOpportunities([]);
    } finally {
      setIsOppLoading(false);
    }
  };

  // Actualizează data ofertei în formular.
  const handleDateChange = (date) => {
    const iso = date ? date.toISOString().split("T")[0] : "";
    setSelectedDate(date);
    setSelOfferDate(iso);

    if (fieldErrors.selOfferDate) {
      setFieldErrors((prev) => ({
        ...prev,
        selOfferDate: "",
      }));
    }
  };

  /* Când se schimbă oportunitățile selectate:
    - păstrăm liniile deja existente
    - adăugăm linii noi pentru cele nou selectate
   */
  const handleOppsMultiChange = (opts) => {
    const selected = opts || [];
    setSelectedOpps(selected);

    setLines((prevLines) => {
      const prevById = new Map(
        prevLines.map((line) => [line.opportunityId, line])
      );
      const next = [];

      for (const opp of selected) {
        const existing = prevById.get(opp.value);

        if (existing) {
          next.push({
            ...existing,
            opportunityLabel: opp.label,
            project_code_text: opp.project_code_text || "",
            subproject_code_text: opp.subproject_code_text || "",
          });
        } else {
          next.push({
            opportunityId: opp.value,
            opportunityLabel: opp.label,
            project_code_text: opp.project_code_text || "",
            subproject_code_text: opp.subproject_code_text || "",
            sum: null,
            currency: null,
            exchange_rate: null,
            calculated: "",
          });
        }
      }

      return next;
    });

    if (fieldErrors.lines) {
      setFieldErrors((prev) => ({
        ...prev,
        lines: "",
      }));
    }
  };

  /* Actualizează o linie și recalculează suma în moneda de sistem.
    Dacă moneda este diferită de cea de sistem, setează automat cursul implicit.
   */
  const updateLine = (opportunityId, patch) => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.opportunityId !== opportunityId) return line;

        const next = { ...line, ...patch };
        const currencyId = next.currency?.value ?? null;

        if (
          currencyId &&
          systemCurrencyId &&
          Number(currencyId) !== Number(systemCurrencyId)
        ) {
          if (!next.exchange_rate) {
            next.exchange_rate = getDefaultExchangeRateOption(currencyId);
          }
        } else {
          next.exchange_rate = null;
        }

        next.calculated = computeSystemAmount(
          next.sum,
          currencyId,
          next.exchange_rate
        );

        return next;
      })
    );
  };

  // Elimină o oportunitate și linia ei asociată.
  const removeOpportunity = (opportunityId) => {
    setSelectedOpps((prev) => prev.filter((o) => o.value !== opportunityId));
    setLines((prev) => prev.filter((l) => l.opportunityId !== opportunityId));
  };

  // Validează formularul și trimite modificările către backend.
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    if (!validateFiles()) return;

    setIsSubmitting(true);

    const newErrors = {};

    if (!selNumber) newErrors.selNumber = "Required";
    if (!selOfferDate) newErrors.selOfferDate = "Required";
    if (!selClientId) newErrors.selClientId = "Required";
    if (!selDescription) newErrors.selDescription = "Required";
    if (!selectedContactPerson) newErrors.selectedContactPerson = "Required";
    if (!selOfferObject.trim()) newErrors.selOfferObject = "Required";
    if (!selExecutionTerm.trim())
      newErrors.selExecutionTerm = "Required";
    if (!lines.length) newErrors.lines = "Select at least one opportunity";

    lines.forEach((line, idx) => {
      if (line.sum != null && line.sum !== "") {
        if (!line.currency) {
          newErrors[`line_${idx}_currency`] = "Currency required when sum is set";
        }

        const currencyId = line.currency?.value ?? null;
        if (
          currencyId &&
          systemCurrencyId &&
          Number(currencyId) !== Number(systemCurrencyId) &&
          !line.exchange_rate
        ) {
          newErrors[`line_${idx}_exchange_rate`] = "Exchange rate required";
        }
      }
    });

    customFields.forEach((field, idx) => {
      const title = (field.field_title || "").trim();
      const content = (field.field_content || "").trim();

      if (content && !title) {
        newErrors[`custom_field_${idx}_title`] = "Field title is required";
      }
    });

    setFieldErrors(newErrors);

    if (Object.keys(newErrors).length) {
      setIsSubmitting(false);
      const firstError = document.querySelector(".FormOfferRegistry-error");
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    try {
      // Dacă numărul s-a schimbat, verificăm mai întâi să nu existe deja.
      if (selNumber !== initialNumber) {
        const checkRes = await axiosInstance.post(
          "/offer-register/seek-offer-code/",
          {
            number: selNumber,
            id,
          }
        );

        const exists =
          checkRes.data === true
            ? true
            : checkRes.data === false
              ? false
              : Boolean(checkRes.data?.exists);

        if (exists) {
          showError("Number already exists.");
          setIsSubmitting(false);
          return;
        }
      }

      const submissionData = new FormData();

      submissionData.append("offer_code", selNumber);
      submissionData.append("offer_date", selOfferDate);
      submissionData.append("client", String(selClientId || ""));
      submissionData.append("status", selStatus ? selStatus.value : "");
      submissionData.append("valability", selValidity ? selValidity.value : "");
      submissionData.append("description", selDescription);
      submissionData.append("offer_object", selOfferObject);
      submissionData.append("execution_term", selExecutionTerm);

      if (selectedContactPerson) {
        submissionData.append("contact_persons", selectedContactPerson.value);
      }

      const offerLinesPayload = lines.map((line) => ({
        opportunity: Number(line.opportunityId),
        sum: line.sum === null || line.sum === "" ? null : Number(line.sum),
        currency: line.currency ? Number(line.currency.value) : null,
        exchange_rate: line.exchange_rate ? Number(line.exchange_rate.value) : null,
      }));

      submissionData.append(
        "offer_opportunities",
        JSON.stringify(offerLinesPayload)
      );

      const customFieldsPayload = customFields
        .map((field, index) => ({
          id: field.id,
          field_title: (field.field_title || "").trim(),
          field_content: field.field_content || "",
          order: index,
        }))
        .filter((field) => field.field_title || field.field_content);

      submissionData.append(
        "custom_fields",
        JSON.stringify(customFieldsPayload)
      );

      if (existingAttachments.length > 0) {
        existingAttachments.forEach((attachment) => {
          if (attachment.id) {
            submissionData.append("keep_attachments", attachment.id);
          }
        });
      }

      if (files.length > 0) {
        files.forEach((file) => {
          submissionData.append("attachments", file);
        });
      }

      await axiosInstance.put(`/offer-register/${id}/`, submissionData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      navigate(-1);
    } catch (error) {
      console.error("Update error:", error.response?.data || error.message);

      if (error.response?.data) {
        const err = error.response.data;

        if (
          err.attachments ||
          err.keep_attachments ||
          (typeof err === "object" &&
            JSON.stringify(err).toLowerCase().includes("file"))
        ) {
          showError("File upload error. Please check file type and size.");
        } else if (typeof err === "object") {
          const firstMessage =
            Object.values(err).flat().join(" | ") || JSON.stringify(err);
          showError(firstMessage);
        } else {
          showError("An error occurred while updating.");
        }
      } else {
        showError("An error occurred while updating.");
      }

      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(false);
  };

  // Componentă mică pentru afișarea erorilor sub câmpuri.
  const ErrorMessage = ({ error }) =>
    error ? (
      <div className="FormOfferRegistry-error">
        <span className="error-icon">⚠️</span>
        <span>{error}</span>
      </div>
    ) : null;

  // Header reutilizabil pentru secțiunile formularului.
  const SectionHeader = ({ title, section }) => (
    <div
      className="FormOfferRegistry-section-header"
      onClick={() => toggleSection(section)}
    >
      <h2>{title}</h2>
      <button type="button" className="toggle-btn">
        {expandedSections[section] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </button>
    </div>
  );

  // Loader cât timp se încarcă datele inițiale.
  if (isLoading) {
    return (
      <div className="FormOfferRegistry">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading offer data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="FormOfferRegistry">
      {errorNotification && (
        <div className="FormOfferRegistry-top-error">{errorNotification}</div>
      )}

      <div className="FormOfferRegistry-header">
        <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
        <h1 className="header-title">EDIT OFFER</h1>
        <div className="offer-code">Number: {selNumber}</div>
      </div>

      <hr className="title-separator" />

      <form onSubmit={handleSubmit} className="FormOfferRegistry-form">
        <div className="FormOfferRegistry-section">
          <SectionHeader title="BASIC INFORMATION" section="basic" />

          {expandedSections.basic && (
            <div className="section-content">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Client <span className="required">*</span>
                  </label>

                  <Select
                    options={clients}
                    classNamePrefix="FormOfferRegistry-select"
                    value={selectedClient}
                    onChange={handleClientChange}
                    placeholder="Select client..."
                    components={animatedComponents}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />

                  <ErrorMessage error={fieldErrors.selClientId} />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Offer Date <span className="required">*</span>
                  </label>

                  <div style={{ position: "relative" }}>
                    <DatePicker
                      selected={selectedDate}
                      onChange={handleDateChange}
                      dateFormat="dd.MM.yyyy"
                      locale="ro"
                      placeholderText="Select date"
                      className="FormOfferRegistry-date-input"
                      popperContainer={({ children }) => (
                        <div style={{ position: "fixed", zIndex: 9999 }}>
                          {children}
                        </div>
                      )}
                    />

                    <span
                      className="calendar-icon"
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </span>
                  </div>

                  <ErrorMessage error={fieldErrors.selOfferDate} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Contact Person <span className="required">*</span>
                  </label>
                  <Select
                    options={clientContacts}
                    classNamePrefix="FormOfferRegistry-select"
                    value={selectedContactPerson}
                    onChange={(opt) => {
                      setSelectedContactPerson(opt);
                      if (fieldErrors.selectedContactPerson) {
                        setFieldErrors((prev) => ({
                          ...prev,
                          selectedContactPerson: "",
                        }));
                      }
                    }}
                    placeholder={
                      !selClientId
                        ? "Select a client first"
                        : "Select contact person..."
                    }
                    components={animatedComponents}
                    isDisabled={!selClientId}
                    isClearable
                    noOptionsMessage={() => "No contacts found"}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />
                  <ErrorMessage error={fieldErrors.selectedContactPerson} />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Execution Term <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="FormOfferRegistry-date-input"
                    value={selExecutionTerm}
                    onChange={(e) => {
                      setSelExecutionTerm(e.target.value);

                      if (fieldErrors.selExecutionTerm) {
                        setFieldErrors((prev) => ({
                          ...prev,
                          selExecutionTerm: "",
                        }));
                      }
                    }}
                    placeholder="Enter execution term..."
                  />
                  <ErrorMessage error={fieldErrors.selExecutionTerm} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Number <span className="required">*</span>
                  </label>

                  <input
                    type="text"
                    value={selNumber}
                    onChange={(e) => {
                      setSelNumber(e.target.value);
                      if (fieldErrors.selNumber) {
                        setFieldErrors((prev) => ({
                          ...prev,
                          selNumber: "",
                        }));
                      }
                    }}
                    className="readonly-field"
                    style={{ backgroundColor: "white", color: "#333", cursor: "text" }}
                    autoComplete="off"
                  />

                  <ErrorMessage error={fieldErrors.selNumber} />
                </div>

                <div className="form-group">
                  <label className="form-label">Status</label>

                  <Select
                    options={statusOptions}
                    classNamePrefix="FormOfferRegistry-select"
                    value={selStatus}
                    onChange={setSelStatus}
                    placeholder="Select status"
                    components={animatedComponents}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Validity (days)</label>

                  <Select
                    options={validityOptions}
                    classNamePrefix="FormOfferRegistry-select"
                    value={selValidity}
                    onChange={setSelValidity}
                    placeholder="Select validity"
                    components={animatedComponents}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />
                </div>
              </div>

              <div
                className="form-group full-width"
                style={{ marginBottom: "10px" }}
              >
                <label className="form-label">
                  Offer Object <span className="required">*</span>
                </label>

                <input
                  type="text"
                  className="FormOfferRegistry-date-input"
                  value={selOfferObject}
                  onChange={(e) => {
                    setSelOfferObject(e.target.value);
                    if (fieldErrors.selOfferObject) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        selOfferObject: "",
                      }));
                    }
                  }}
                  placeholder="Enter offer object..."
                />

                <ErrorMessage error={fieldErrors.selOfferObject} />
              </div>

              <div className="form-group full-width">
                <label className="form-label">
                  Description <span className="required">*</span>
                </label>

                <textarea
                  className="FormOfferRegistry-description"
                  value={selDescription}
                  onChange={(e) => {
                    setSelDescription(e.target.value);
                    if (fieldErrors.selDescription) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        selDescription: "",
                      }));
                    }
                  }}
                  placeholder="Enter offer description, terms, conditions, or any additional notes..."
                  rows={3}
                />

                <ErrorMessage error={fieldErrors.selDescription} />
              </div>
            </div>
          )}
        </div>

        <div className="FormOfferRegistry-section">
          <SectionHeader title="OPPORTUNITIES & PRICING" section="opportunities" />

          {expandedSections.opportunities && (
            <div className="section-content">
              <div className="form-group full-width">
                <label className="form-label">
                  Select Opportunities <span className="required">*</span>
                </label>

                <Select
                  isMulti
                  options={opportunities}
                  classNamePrefix="FormOfferRegistry-select"
                  value={selectedOpps}
                  onChange={handleOppsMultiChange}
                  placeholder={
                    !selClientId
                      ? "Select a client first"
                      : isOppLoading
                        ? "Loading opportunities..."
                        : "Search and select opportunities..."
                  }
                  components={animatedComponents}
                  isDisabled={!selClientId || isOppLoading}
                  noOptionsMessage={() => "No opportunities found"}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectPortalStyles}
                />

                <ErrorMessage error={fieldErrors.lines} />
              </div>

              {lines.length === 0 ? (
                <div className="no-opportunities-message">
                  <AddIcon className="hint-icon" />
                  <span>No opportunities selected yet</span>
                </div>
              ) : (
                <div className="opportunities-grid">
                  {lines.map((line, idx) => {
                    const currencyId = line.currency?.value ?? null;
                    const exchangeOpts = currencyId
                      ? exchangeOptionsByCurrencyId.get(currencyId) || []
                      : [];

                    return (
                      <div key={line.opportunityId} className="opportunity-item">
                        <div className="opportunity-item-header">
                          <div className="item-title">
                            <h3>{line.opportunityLabel}</h3>
                          </div>

                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => removeOpportunity(line.opportunityId)}
                            title="Remove opportunity"
                          >
                            <DeleteIcon />
                          </button>
                        </div>

                        <div className="opportunity-details">
                          <div className="detail-row">
                            <div className="detail-field">
                              <label className="form-label">Project Code</label>
                              <input
                                type="text"
                                value={line.project_code_text}
                                readOnly
                                className="readonly-field"
                              />
                            </div>

                            <div className="detail-field">
                              <label className="form-label">Subproject Code</label>
                              <input
                                type="text"
                                value={line.subproject_code_text}
                                readOnly
                                className="readonly-field"
                              />
                            </div>
                          </div>

                          <div className="pricing-row">
                            <div className="pricing-field">
                              <label className="form-label">Value (Original currency)</label>

                              <NumericFormat
                                value={line.sum}
                                onValueChange={(values) => {
                                  const val =
                                    values.floatValue !== undefined
                                      ? values.floatValue
                                      : null;
                                  updateLine(line.opportunityId, { sum: val });
                                }}
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={2}
                                fixedDecimalScale
                                allowNegative={false}
                                placeholder="0,00"
                                className="amount-input"
                              />
                            </div>

                            <div className="pricing-field">
                              <label className="form-label">Currency</label>

                              <Select
                                options={currencies}
                                classNamePrefix="FormOfferRegistry-select"
                                value={line.currency}
                                onChange={(opt) =>
                                  updateLine(line.opportunityId, { currency: opt })
                                }
                                placeholder="Select"
                                components={animatedComponents}
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={selectPortalStyles}
                              />

                              <ErrorMessage error={fieldErrors[`line_${idx}_currency`]} />
                            </div>

                            <div className="pricing-field">
                              <label className="form-label">Exchange Rate</label>

                              <Select
                                options={exchangeOpts}
                                classNamePrefix="FormOfferRegistry-select"
                                value={line.exchange_rate}
                                onChange={(opt) =>
                                  updateLine(line.opportunityId, {
                                    exchange_rate: opt,
                                  })
                                }
                                formatOptionLabel={(option, { context }) =>
                                  context === "value"
                                    ? option.shortLabel || option.label
                                    : option.label
                                }
                                placeholder={
                                  !line.currency
                                    ? "Select currency first"
                                    : Number(currencyId) === Number(systemCurrencyId)
                                      ? "Not needed"
                                      : exchangeOpts.length
                                        ? "Select rate"
                                        : "No rates"
                                }
                                components={animatedComponents}
                                isClearable
                                isDisabled={
                                  !line.currency ||
                                  Number(currencyId) === Number(systemCurrencyId)
                                }
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={selectPortalStyles}
                              />

                              <ErrorMessage
                                error={fieldErrors[`line_${idx}_exchange_rate`]}
                              />
                            </div>

                            <div className="pricing-field">
                              <label className="form-label">
                                Value
                                {systemCurrencyLabel
                                  ? ` (${systemCurrencyLabel})`
                                  : ""}
                              </label>

                              <NumericFormat
                                value={line.calculated}
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={2}
                                fixedDecimalScale
                                placeholder="0,00"
                                className="amount-input"
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="FormOfferRegistry-section">
          <SectionHeader title="ADDITIONAL FIELDS" section="additionalFields" />

          {expandedSections.additionalFields && (
            <div className="section-content">
              <div className="form-group full-width">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "14px",
                  }}
                >
                  <div>
                    <p className="file-hint" style={{ marginTop: 4 }}>
                      Add optional custom fields that will be saved together with this offer.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="additional-fields-add-btn"
                    onClick={addCustomField}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <AddIcon fontSize="small" />
                    Add Field
                  </button>
                </div>

                {customFields.length === 0 ? (
                  <div
                    style={{
                      border: "1px dashed #cbd5e1",
                      borderRadius: "10px",
                      padding: "16px",
                      color: "#64748b",
                      background: "#f8fafc",
                    }}
                  >
                    No additional fields added.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                    {customFields.map((field, index) => (
                      <div
                        key={field.localId}
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: "12px",
                          padding: "14px",
                          background: "#ffffff",
                        }}
                      >
                        <div className="form-row" style={{ alignItems: "flex-start" }}>
                          <div className="form-group">
                            <label className="form-label">Field Title</label>
                            <input
                              type="text"
                              className="FormOfferRegistry-input"
                              placeholder="Enter field title"
                              value={field.field_title}
                              onChange={(e) =>
                                updateCustomField(field.localId, {
                                  field_title: e.target.value,
                                })
                              }
                            />
                            <ErrorMessage error={fieldErrors[`custom_field_${index}_title`]} />
                          </div>

                          <div className="form-group">
                            <label className="form-label">Field Content</label>
                            <input
                              type="text"
                              className="FormOfferRegistry-input additional-field-content"
                              placeholder="Enter field content"
                              value={field.field_content}
                              onChange={(e) =>
                                updateCustomField(field.localId, {
                                  field_content: e.target.value,
                                })
                              }
                            />
                          </div>

                          <button
                            type="button"
                            className="remove-file-button"
                            onClick={() => removeCustomField(field.localId)}
                            title="Remove field"
                            style={{ marginTop: "28px" }}
                          >
                            <DeleteIcon fontSize="small" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="FormOfferRegistry-section">
          <SectionHeader title="ATTACHMENTS" section="attachments" />

          {expandedSections.attachments && (
            <div className="section-content">
              <div className="form-group full-width">
                <label className="form-label">Attachments</label>

                {existingAttachments.length > 0 && (
                  <div className="files-preview-container" style={{ marginBottom: "14px" }}>
                    <h4>Current Files ({existingAttachments.length})</h4>
                    <div className="files-list">
                      {existingAttachments.map((attachment, index) => (
                        <div
                          key={attachment.id || index}
                          className="file-preview-item existing"
                        >
                          <div className="file-info">
                            {getFileIcon(attachment, true)}
                            <span
                              className="file-name clickable"
                              title={
                                isPdfExistingFile(attachment)
                                  ? "Click to preview PDF"
                                  : "Click to download file"
                              }
                              onClick={() => handleFileClick(attachment, true)}
                            >
                              {attachment.filename?.length > 40
                                ? `${attachment.filename.substring(0, 40)}...`
                                : attachment.filename || "Unknown file"}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="remove-file-button"
                            onClick={() => handleRemoveFile(index, true)}
                            title="Remove file"
                          >
                            <DeleteIcon fontSize="small" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div key={dropzoneKey} className="dropzone-wrapper">
                  <div {...getRootProps({ className: "dropzone" })}>
                    <input {...getInputProps()} />
                    <div className="dropzone-content">
                      {isDragActive ? (
                        <p>Drop the files here...</p>
                      ) : (
                        <p>Drag & drop files or click to select</p>
                      )}
                      <CloudUploadIcon style={{ fontSize: 40, color: "#888" }} />
                    </div>
                  </div>

                  {files.length > 0 && (
                    <div className="files-preview-container">
                      <h4>New Files ({files.length})</h4>
                      <div className="files-list">
                        {files.map((file, index) => (
                          <div key={index} className="file-preview-item">
                            <div className="file-info">
                              {getFileIcon(file)}
                              <span
                                className="file-name clickable"
                                title={
                                  file.type === "application/pdf"
                                    ? "Click to preview PDF"
                                    : "Click to download file"
                                }
                                onClick={() => handleFileClick(file)}
                              >
                                {file.name.length > 40
                                  ? `${file.name.substring(0, 40)}...`
                                  : file.name}
                              </span>
                              <span className="file-size">
                                {(file.size / 1024).toFixed(1)} KB
                              </span>
                            </div>
                            <button
                              type="button"
                              className="remove-file-button"
                              onClick={() => handleRemoveFile(index)}
                              title="Remove file"
                            >
                              <DeleteIcon fontSize="small" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <p className="file-hint">
                  Accepted file types: PDF, Word, Excel, Images, Text. Multiple files
                  allowed (max 100MB per file).
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="offer-total-preview">
          <span className="offer-total-label">Total:</span>
          <span className="offer-total-value">{liveTotalDisplay}</span>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="secondary-btn"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>

          <button type="submit" className="primary-btn" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                Updating...
              </>
            ) : (
              "Update"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditOfferRegistry;